import { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { formatDate, formatDateTime } from '../utils/dateFormatter'
import { debounce } from '../utils/debounce'
import { useAuth } from '../context/AuthContext'
import franchiseService from '../services/franchiseService'
import './QRCodes.css'
import './FranchiseTags.css'

const escapeHtml = (s) => {
    if (s == null || s === '') return ''
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

const tagPrintRef = (codeUuid) =>
    (codeUuid || '').replace(/-/g, '').slice(-8).toUpperCase() || '—'

const applyPrintIdFilters = (items, { parity, startId, endId }) => {
    let out = [...items]
    const parseOpt = (v) => {
        if (v === '' || v == null) return null
        const n = parseInt(String(v), 10)
        return Number.isNaN(n) ? null : n
    }
    let start = parseOpt(startId)
    let end = parseOpt(endId)
    if (start != null && end != null && start > end) {
        const t = start
        start = end
        end = t
    }
    if (start != null) {
        out = out.filter((q) => q.id >= start)
    }
    if (end != null) {
        out = out.filter((q) => q.id <= end)
    }
    if (parity === 'odd') {
        out = out.filter((q) => q.id % 2 === 1)
    } else if (parity === 'even') {
        out = out.filter((q) => q.id % 2 === 0)
    }
    return out
}

const buildPrintFilterSummary = ({ parity, startId, endId }) => {
    const parts = []
    if (parity === 'odd') parts.push('Odd tag IDs only')
    else if (parity === 'even') parts.push('Even tag IDs only')
    const parseOpt = (v) => {
        if (v === '' || v == null) return null
        const n = parseInt(String(v), 10)
        return Number.isNaN(n) ? null : n
    }
    let s = parseOpt(startId)
    let e = parseOpt(endId)
    if (s != null && e != null && s > e) {
        const t = s
        s = e
        e = t
    }
    if (s != null || e != null) {
        parts.push(`ID range ${s ?? '…'}–${e ?? '…'}`)
    }
    return parts.length ? parts.join(' · ') : 'No ID filter (selected tags)'
}

const parsePrintIdRangeBounds = (startId, endId) => {
    const parseNum = (v) => {
        if (v === '' || v == null) return null
        const n = parseInt(String(v).trim(), 10)
        return Number.isNaN(n) || !Number.isFinite(n) ? null : n
    }
    const s = parseNum(startId)
    const e = parseNum(endId)
    if (s == null || e == null) return null
    return { lo: Math.min(s, e), hi: Math.max(s, e) }
}

const PRINT_MODAL_LIST_CAP = 80

const FranchiseTags = () => {
    const navigate = useNavigate()
    const { user, isTagFranchise } = useAuth()
    const [qrcodes, setQrcodes] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedCodes, setSelectedCodes] = useState(new Set())
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    })
    const [filters, setFilters] = useState({
        is_active: '',
        is_car: '',
        search: ''
    })
    const [searchInput, setSearchInput] = useState('')
    const [qrCodePreviews, setQrCodePreviews] = useState({})
    const [generatingPreviews, setGeneratingPreviews] = useState(new Set())
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false)
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
    const [requestForm, setRequestForm] = useState({
        bikeCount: 50,
        carCount: 50,
        message: '',
        shippingAddress: ''
    })
    const [requestSubmitting, setRequestSubmitting] = useState(false)
    const [printOptions, setPrintOptions] = useState({
        type: 'bike',
        copies: 1
    })
    const [printIdFilter, setPrintIdFilter] = useState({
        parity: 'all',
        startId: '',
        endId: ''
    })
    const [rangePreviewTags, setRangePreviewTags] = useState([])
    const [rangePreviewLoading, setRangePreviewLoading] = useState(false)
    const [rangePreviewTruncated, setRangePreviewTruncated] = useState(false)
    const [rangePreviewMax, setRangePreviewMax] = useState(1000)
    const previewsRef = useRef({})

    const idRangeBounds = useMemo(
        () => parsePrintIdRangeBounds(printIdFilter.startId, printIdFilter.endId),
        [printIdFilter.startId, printIdFilter.endId]
    )

    const tagsMatchingPrintFilters = useMemo(
        () =>
            applyPrintIdFilters(
                qrcodes.filter((q) => selectedCodes.has(q.id)),
                printIdFilter
            ),
        [qrcodes, selectedCodes, printIdFilter]
    )

    const tagsForPrintModal = idRangeBounds ? rangePreviewTags : tagsMatchingPrintFilters
    const printModalListPreview = useMemo(
        () => tagsForPrintModal.slice(0, PRINT_MODAL_LIST_CAP),
        [tagsForPrintModal]
    )
    const printModalListOmit = Math.max(0, tagsForPrintModal.length - PRINT_MODAL_LIST_CAP)

    useEffect(() => {
        if (!isPrintModalOpen) {
            setRangePreviewTags([])
            setRangePreviewTruncated(false)
            setRangePreviewLoading(false)
            return
        }
        if (!idRangeBounds) {
            setRangePreviewTags([])
            setRangePreviewTruncated(false)
            setRangePreviewLoading(false)
            return
        }
        let cancelled = false
        setRangePreviewLoading(true)
        const t = setTimeout(async () => {
            try {
                const res = await api.get('/qrcodes/print/by-id-range', {
                    params: {
                        start_id: idRangeBounds.lo,
                        end_id: idRangeBounds.hi,
                        parity: printIdFilter.parity
                    }
                })
                if (cancelled) return
                if (res.data.success && res.data.data) {
                    setRangePreviewTags(res.data.data.qrcodes || [])
                    setRangePreviewTruncated(!!res.data.data.truncated)
                    if (res.data.data.max) setRangePreviewMax(res.data.data.max)
                } else {
                    setRangePreviewTags([])
                    setRangePreviewTruncated(false)
                }
            } catch (err) {
                if (!cancelled) {
                    setRangePreviewTags([])
                    setRangePreviewTruncated(false)
                    toast.error(err.response?.data?.message || 'Could not load tags for this range')
                }
            } finally {
                if (!cancelled) setRangePreviewLoading(false)
            }
        }, 400)
        return () => {
            cancelled = true
            clearTimeout(t)
        }
    }, [isPrintModalOpen, idRangeBounds, printIdFilter.parity])

    // Memoize filters to prevent unnecessary re-renders
    const memoizedFilters = useMemo(() => filters, [
        filters.is_active,
        filters.is_car,
        filters.search
    ])

    // Debounced search handler
    const debouncedSearch = useMemo(
        () => debounce((value) => {
            setFilters(prev => ({ ...prev, search: value }))
            setPagination(prev => ({ ...prev, page: 1 }))
        }, 500),
        []
    )

    // Handle search input change
    const handleSearchChange = useCallback((e) => {
        const value = e.target.value
        setSearchInput(value)
        debouncedSearch(value)
    }, [debouncedSearch])

    const fetchQRCodes = useCallback(async () => {
        try {
            setLoading(true)
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...(memoizedFilters.is_active && { is_active: memoizedFilters.is_active }),
                ...(memoizedFilters.is_car && { is_car: memoizedFilters.is_car }),
                ...(memoizedFilters.search && { search: memoizedFilters.search })
            })

            const response = await api.get(`/qrcodes/franchise/my-tags?${params}`)
            if (response.data.success) {
                setQrcodes(response.data.data.qrcodes)
                setPagination(response.data.data.pagination)
            }
        } catch (error) {
            console.error('Error fetching QR codes:', error)
        } finally {
            setLoading(false)
        }
    }, [pagination.page, pagination.limit, memoizedFilters])

    useEffect(() => {
        fetchQRCodes()
    }, [fetchQRCodes])

    // Update ref when previews change
    useEffect(() => {
        previewsRef.current = qrCodePreviews
    }, [qrCodePreviews])

    // Lazy load QR code previews 
    useEffect(() => {
        if (qrcodes.length === 0) {
            setQrCodePreviews({})
            previewsRef.current = {}
            setGeneratingPreviews(new Set())
            return
        }

        let cancelled = false
        const qrIds = new Set(qrcodes.map(qr => qr.id))

        const generatePreviewsBatch = async (batch) => {
            if (cancelled) return

            const newPreviews = {}

            for (const qr of batch) {
                if (cancelled) break

                if (previewsRef.current[qr.id]) {
                    continue
                }

                setGeneratingPreviews(prev => {
                    if (prev.has(qr.id)) return prev
                    return new Set(prev).add(qr.id)
                })

                try {
                    const qrUrl = `${window.location.origin}/qr/${qr.code_uuid}`
                    const qrDataUrl = await QRCode.toDataURL(qrUrl, {
                        width: 80,
                        margin: 1,
                        color: {
                            dark: '#000000',
                            light: '#FFFFFF'
                        }
                    })

                    if (!cancelled && qrIds.has(qr.id) && !previewsRef.current[qr.id]) {
                        newPreviews[qr.id] = qrDataUrl
                    }
                } catch (error) {
                    console.error('Error generating QR preview:', error)
                } finally {
                    if (!cancelled) {
                        setGeneratingPreviews(prev => {
                            const next = new Set(prev)
                            next.delete(qr.id)
                            return next
                        })
                    }
                }
            }

            if (!cancelled && Object.keys(newPreviews).length > 0) {
                setQrCodePreviews(prev => {
                    const updated = { ...prev, ...newPreviews }
                    previewsRef.current = updated
                    return updated
                })
            }
        }

        const batchSize = 3
        const timeouts = []
        for (let i = 0; i < qrcodes.length; i += batchSize) {
            const batch = qrcodes.slice(i, i + batchSize)
            const timeout = setTimeout(() => {
                if (!cancelled) {
                    generatePreviewsBatch(batch)
                }
            }, i * 100)
            timeouts.push(timeout)
        }

        return () => {
            cancelled = true
            timeouts.forEach(timeout => clearTimeout(timeout))
        }
    }, [qrcodes])

    const handleSelectAll = useCallback((e) => {
        if (e.target.checked) {
            const allIds = new Set(qrcodes.map(qr => qr.id))
            setSelectedCodes(allIds)
        } else {
            setSelectedCodes(new Set())
        }
    }, [qrcodes])

    const handleSelectOne = useCallback((id) => {
        setSelectedCodes(prev => {
            const newSelected = new Set(prev)
            if (newSelected.has(id)) {
                newSelected.delete(id)
            } else {
                newSelected.add(id)
            }
            return newSelected
        })
    }, [])

    const handlePrint = () => {
        setIsPrintModalOpen(true)
    }

    const executePrint = async () => {
        const bounds = parsePrintIdRangeBounds(printIdFilter.startId, printIdFilter.endId)
        let selectedQRs
        if (bounds) {
            try {
                const res = await api.get('/qrcodes/print/by-id-range', {
                    params: {
                        start_id: bounds.lo,
                        end_id: bounds.hi,
                        parity: printIdFilter.parity
                    }
                })
                if (!res.data.success || !res.data.data) {
                    toast.error(res.data.message || 'Could not load tags for printing')
                    return
                }
                const { qrcodes: list, truncated, max } = res.data.data
                if (truncated) {
                    toast.error(`Too many tags in this range (over ${max}). Narrow From/To or use odd/even.`)
                    return
                }
                if (!list?.length) {
                    toast.error('No tags in this range.')
                    return
                }
                selectedQRs = list
            } catch (err) {
                toast.error(err.response?.data?.message || 'Could not load tags for printing')
                return
            }
        } else {
            selectedQRs = tagsMatchingPrintFilters
            if (selectedQRs.length === 0) {
                toast.error('Enter both From and To to print by database ID range, or select tags in the table.')
                return
            }
        }
        const { type, copies } = printOptions
        const printFilterLine = buildPrintFilterSummary(printIdFilter)

        const config = {
            bike: {
                width: '4in',
                height: '2.5in',
                cols_final: 4,
                rows_final: 5,
                sheetWidth: '19in',
                sheetHeight: '13in'
            },
            car: {
                width: '4in',
                height: '2.5in',
                cols_final: 4,
                rows_final: 5,
                sheetWidth: '19in',
                sheetHeight: '13in'
            }
        }[type]

        const qrCodeImages = await Promise.all(
            selectedQRs.map(async (qr) => {
                try {
                    const qrUrl = `${window.location.origin}/qr/${qr.code_uuid}`
                    const qrDataUrl = await QRCode.toDataURL(qrUrl, {
                        width: 400,
                        margin: 1,
                        color: {
                            dark: '#000000',
                            light: '#FFFFFF'
                        }
                    })
                    return { ...qr, qrImage: qrDataUrl }
                } catch (error) {
                    console.error('Error generating QR code:', error)
                    return { ...qr, qrImage: null }
                }
            })
        )

        const finalPrintList = []
        qrCodeImages.forEach(qr => {
            for (let i = 0; i < copies; i++) {
                finalPrintList.push(qr)
            }
        })

        const perSheet = config.cols_final * config.rows_final
        const pages = []
        for (let i = 0; i < finalPrintList.length; i += perSheet) {
            pages.push(finalPrintList.slice(i, i + perSheet))
        }

        const printWindow = window.open('', '_blank')
        if (!printWindow) {
            toast.error('Could not open print window. Allow popups for this site and try again.')
            return
        }

        const printedAt = new Date().toLocaleString()
        const typeLabel = type === 'car' ? 'Car (2.5" × 4")' : 'Bike (2.5" × 4")'
        const summaryRows = selectedQRs
            .map((qr, idx) => {
                const vehicle = qr.linking?.vehicle_number || '—'
                const owner = qr.linking
                    ? [qr.linking.first_name, qr.linking.last_name].filter(Boolean).join(' ') || qr.linking.mobile || '—'
                    : qr.owner_business_name || qr.owner_name || '—'
                return `<tr>
          <td>${idx + 1}</td>
          <td>${qr.id}</td>
          <td><code>${escapeHtml(qr.code_uuid)}</code></td>
          <td>${escapeHtml(vehicle)}</td>
          <td>${escapeHtml(String(owner))}</td>
          <td>${copies}</td>
        </tr>`
            })
            .join('')

        const docTitle = `NAH44 print — ${selectedQRs.length} tag(s) × ${copies} copy(ies) — ${pages.length} sheet(s)`

        const iconStroke = '#000'
        const iconFill = '#fff'
        const iconParking = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 10h2v6H7zM15 10h2v6h-2z"/></svg>'
        const iconRegistered = '<span class="tag-icon-circle">&#9415;</span>'
        const iconWarning = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5"><path d="M12 8v4M12 16h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>'
        const iconPhone = '<svg class="tag-icon" viewBox="0 0 24 24" fill="' + iconFill + '" stroke="' + iconStroke + '" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>'
        const indiaFlagSvg = '<svg class="india-flag-svg" viewBox="0 0 21 14" xmlns="http://www.w3.org/2000/svg"><rect width="21" height="4.67" fill="#FF9933"/><rect y="4.67" width="21" height="4.66" fill="#fff"/><rect y="9.33" width="21" height="4.67" fill="#138808"/><circle cx="10.5" cy="7" r="1.5" fill="#000080"/><circle cx="10.5" cy="7" r="1.1" fill="#fff" opacity="0.95"/></svg>'
        printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(docTitle)}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            
            body { 
              background: #f0f0f0;
              font-family: 'Outfit', sans-serif;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .print-job-summary {
              max-width: 10in;
              margin: 16px auto 24px;
              padding: 16px 20px;
              background: #fff;
              border: 1px solid #ccc;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.08);
              font-size: 12px;
              color: #222;
            }
            .print-job-summary h1 {
              font-size: 16px;
              margin: 0 0 10px 0;
              color: #1A365D;
            }
            .print-job-summary .meta { margin-bottom: 12px; line-height: 1.5; color: #444; }
            .print-job-summary table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 8px;
              font-size: 11px;
            }
            .print-job-summary th, .print-job-summary td {
              border: 1px solid #ddd;
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
            }
            .print-job-summary th { background: #f5f5f5; font-weight: 700; }
            .print-job-summary code { font-size: 10px; word-break: break-all; }

            .tag-ref-print {
              font-size: ${type === 'car' ? '6px' : '5px'};
              font-family: ui-monospace, monospace;
              color: #333;
              text-align: center;
              margin-top: 2px;
              letter-spacing: 0.04em;
              font-weight: 700;
              line-height: 1.1;
            }

            .sheet {
              width: ${config.sheetWidth};
              height: ${config.sheetHeight};
              background: white;
              margin: 20px auto;
              padding: 0.25in;
              display: grid;
              grid-template-columns: repeat(${config.cols_final}, ${config.width});
              grid-template-rows: repeat(${config.rows_final}, ${config.height});
              gap: 5px 5px;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
              page-break-after: always;
            }

            .tag-card {
              width: ${config.width};
              height: ${config.height};
              display: flex;
              border: none;
              overflow: hidden;
              position: relative;
              border-radius: 0;
              page-break-inside: avoid;
              break-inside: avoid;
            }

            .left-panel {
              width: 42%;
              background: #FF8C00 !important;
              padding: ${type === 'car' ? '12px 10px' : '8px 6px'};
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: space-between;
              -webkit-print-color-adjust: exact !important;
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }

            .white-qr-panel {
              width: 100%;
              background: white !important;
              border: 4px solid #FF8C00 !important;
              border-radius: 0;
              padding: 5px;
              display: flex;
              flex-direction: column;
              align-items: center;
              -webkit-print-color-adjust: exact !important;
            }
            .qr-wrapper {
              width: 88%;
              aspect-ratio: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              border: none;
              padding: 0;
              border-radius: 0;
              overflow: hidden;
            }

            .qr-wrapper img {
              width: 100%;
              height: 100%;
              display: block;
              border-radius: 0;
            }

            .tag-branding-mini {
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: center;
              margin-top: 5px;
              padding-top: 4px;
            }
            .mini-logo-box {
              border: 1px solid #E89B2C !important;
              border-radius: 0;
              padding: 3px 8px;
              background: rgba(255,250,240,0.95) !important;
              display: inline-flex;
              align-items: center;
              gap: 0;
              white-space: nowrap;
              -webkit-print-color-adjust: exact !important;
            }
            .mini-logo-nah {
              color: #FF8C00 !important;
              font-weight: 900;
              font-size: ${type === 'car' ? '12px' : '10px'};
              text-shadow: 0 0 1px rgba(0,0,0,0.2);
            }
            .mini-logo-44 {
              color: #00458B !important;
              font-weight: 900;
              font-size: ${type === 'car' ? '12px' : '10px'};
              text-shadow: 0 0 1px rgba(0,0,0,0.2);
            }
            .india-flag-below { display: inline-flex; align-items: center; justify-content: center; }
            .india-flag-svg { width: ${type === 'car' ? '24px' : '20px'}; height: auto; display: block; border: 0.5px solid rgba(0,0,0,0.12); border-radius: 0; flex-shrink: 0; }

            .icons-row {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: ${type === 'car' ? '8px' : '6px'};
              margin: 6px 0 2px 0;
            }
            .icons-row .tag-icon { width: ${type === 'car' ? '18px' : '14px'}; height: ${type === 'car' ? '18px' : '14px'}; display: block; }
            .icons-row .tag-icon-circle {
              font-size: ${type === 'car' ? '14px' : '12px'} !important;
              display: inline-flex; align-items: center; justify-content: center;
              width: ${type === 'car' ? '20px' : '16px'}; height: ${type === 'car' ? '20px' : '16px'};
              border: 1.5px solid #000;
              border-radius: 50%;
              background: #fff !important;
              color: #000 !important;
              font-weight: bold;
              -webkit-print-color-adjust: exact !important;
            }

            .bottom-text-left {
              font-size: ${type === 'car' ? '7px' : '6px'};
              text-align: center;
              line-height: 1.2;
              font-weight: 800;
              color: #fff !important;
              letter-spacing: 0.01em;
              padding: 0 4px;
              -webkit-print-color-adjust: exact !important;
            }
            .bottom-text-left .line1 { font-size: 1.1em; font-weight: 900; display: block; margin-bottom: 1px; }

            .right-panel {
              width: 58%;
              background: white !important;
              padding: ${type === 'car' ? '14px 16px 14px 14px' : '10px 12px 10px 12px'};
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              gap: 4px;
              position: relative;
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            }

            .right-header { margin-bottom: 14px; }

            .nah-brand-framed {
              border: 3px solid #E89B2C !important;
              border-radius: 0;
              padding: 8px 16px;
              display: inline-flex;
              align-items: center;
              gap: 2px;
              font-weight: 900;
              font-size: ${type === 'car' ? '22px' : '17px'};
              background: linear-gradient(180deg, #f5f0ea 0%, #e8e4df 100%) !important;
              box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), inset 0 0 0 1px rgba(255,255,255,0.6), 2px 2px 4px rgba(0,0,0,0.2), -1px -1px 2px rgba(255,255,255,0.4);
              white-space: nowrap;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .brand-nah {
              color: #FF8C00 !important;
              text-shadow: 2px 2px 3px rgba(0,0,0,0.35), 1px 1px 0 rgba(0,0,0,0.2);
            }
            .brand-44 {
              color: #00458B !important;
              text-shadow: 2px 2px 3px rgba(0,0,0,0.35), 1px 1px 0 rgba(0,0,0,0.2);
            }
            .scan-call-to-action {
              font-size: ${type === 'car' ? '21px' : '16px'};
              font-weight: 950;
              color: #1A479F !important;
              line-height: 1.05;
              margin: 0 0 4px 0;
              letter-spacing: -0.01em;
              text-transform: uppercase;
              font-family: 'Outfit', sans-serif;
            }
            .scan-call-to-action .highlight {
              color: #FF8C00 !important;
              -webkit-print-color-adjust: exact !important;
            }
            .scan-call-to-action .underline {
              text-decoration: none;
              border-bottom: ${type === 'car' ? '4px' : '3px'} solid #FF8C00;
              padding-bottom: 2px;
              display: inline-block;
            }
            .small-instructions {
              font-size: ${type === 'car' ? '7.5px' : '6.5px'};
              color: #333;
              line-height: 1.25;
              max-width: 95%;
              text-transform: uppercase;
              font-weight: 700;
              margin-top: ${type === 'car' ? '5px' : '3px'};
              letter-spacing: -0.1px;
            }

            @media print {
              body { background: transparent; }
              .print-job-summary { display: none !important; }
              .sheet { 
                margin: 0; 
                box-shadow: none; 
                page-break-after: always;
                page-break-inside: avoid;
              }
              .sheet:last-child { page-break-after: auto; }
              .tag-card { page-break-inside: avoid; break-inside: avoid; }
              @page {
                size: ${config.sheetWidth} ${config.sheetHeight};
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-job-summary">
            <h1>Print job summary (not printed — for your records)</h1>
            <div class="meta">
              <strong>Printed:</strong> ${escapeHtml(printedAt)}<br/>
              <strong>Layout:</strong> ${escapeHtml(typeLabel)} · Sheet 13×19 in · <strong>${pages.length}</strong> sticker sheet(s)<br/>
              <strong>ID filter:</strong> ${escapeHtml(printFilterLine)}<br/>
              <strong>Unique tags:</strong> ${selectedQRs.length} · <strong>Copies per tag:</strong> ${copies} · <strong>Total stickers:</strong> ${finalPrintList.length}
            </div>
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Tag ID</th><th>UUID</th><th>Vehicle</th><th>Owner / note</th><th>Copies</th>
                </tr>
              </thead>
              <tbody>${summaryRows}</tbody>
            </table>
            <p style="margin-top:10px;font-size:10px;color:#666;">Each sticker shows ref <strong>TAG · last 8</strong> of UUID (no hyphens) under the QR for matching to this list.</p>
          </div>
          <div class="sticker-sheets">
          ${pages.map(page => `
          <div class="sheet">
            ${page.map(qr => `
              <div class="tag-card">
                <div class="left-panel">
                  <div class="white-qr-panel">
                    <div class="qr-wrapper">
                      ${qr.qrImage ? `<img src="${qr.qrImage}" alt="QR" />` : ''}
                    </div>
                    <div class="tag-ref-print">TAG · ${tagPrintRef(qr.code_uuid)}</div>
                    <div class="tag-branding-mini">
                      <span class="mini-logo-box"><span class="mini-logo-nah">NAH</span><span class="mini-logo-44">44</span></span>
                      <span class="india-flag-below">${indiaFlagSvg}</span>
                    </div>
                  </div>
                  <div class="icons-row">
                    ${iconParking} ${iconRegistered} ${iconWarning} ${iconPhone}
                  </div>
                  <div class="bottom-text-left">
                    <span class="line1">Wrong Parking. Emergency Contact.</span><br/>
                    any issue with the vehicle, Scan the QR.
                  </div>
                </div>
                <div class="right-panel">
                  <div class="right-header">
                    <div class="nah-brand-framed">
                      <span class="brand-nah">NAH</span><span class="brand-44">44</span>
                    </div>
                  </div>
                  <div class="scan-call-to-action">
                    Scan the code<br/>
                    to contact the<br/>
                    <span class="underline"><span class="highlight">vehicle</span> owner.</span>
                  </div>
                  <div class="small-instructions">
                    SCAN USING CAMERA, GOOGLE LENS, OR ANY QR/UPI APP. VISIT NAH44.COM FOR MORE INFO.
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
          `).join('')}
          </div>
        </body>
      </html>
    `)
        printWindow.document.close()
        const idsPrinted = [...new Set(selectedQRs.map((q) => q.id))]
        setTimeout(() => {
            printWindow.print()
            setIsPrintModalOpen(false)
            toast.success(
                `Print: ${selectedQRs.length} tag(s) × ${copies} copy(ies) = ${finalPrintList.length} stickers · ${pages.length} sheet(s) · ${typeLabel}`
            )
            void (async () => {
                try {
                    await api.post('/qrcodes/print-record', { ids: idsPrinted })
                    await fetchQRCodes()
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Could not save last printed time.')
                }
            })()
        }, 1000)
    }

    const handleRequestTags = async (e) => {
        e.preventDefault()

        // Check if user already has a role other than 'user'
        if (user && user.role && user.role !== 'user') {
            alert(`You already have the role: ${user.role}. You cannot apply for additional franchise access.`);
            setIsRequestModalOpen(false);
            return;
        }

        try {
            setRequestSubmitting(true)
            // Convert to snake_case for backend
            const payload = {
                bike_count: requestForm.bikeCount,
                car_count: requestForm.carCount,
                shipping_address: requestForm.shippingAddress,
                message: requestForm.message
            }
            const response = await franchiseService.requestTags(payload)
            if (response.success) {
                alert('Tag request submitted successfully! We will contact you soon.')
                setIsRequestModalOpen(false)
                setRequestForm({ bikeCount: 50, carCount: 50, message: '', shippingAddress: '' })
                // Optionally navigate or show status
            }
        } catch (error) {
            console.error('Error requesting tags:', error)
            alert(error.message || 'Failed to submit tag request')
        } finally {
            setRequestSubmitting(false)
        }
    }

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }))
    }

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }))
        setPagination(prev => ({ ...prev, page: 1 }))
    }

    const QRTableRow = memo(({ qr, isSelected, onSelect }) => (
        <tr>
            <td>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onSelect}
                />
            </td>
            <td>{qr.id}</td>
            <td>
                {qrCodePreviews[qr.id] ? (
                    <img
                        src={qrCodePreviews[qr.id]}
                        alt="QR Code"
                        className="qr-code-preview"
                        loading="lazy"
                    />
                ) : (
                    <div className="qr-code-placeholder">
                        {generatingPreviews.has(qr.id) ? 'Generating...' : 'Loading...'}
                    </div>
                )}
            </td>
            <td>
                <code className="code-uuid">{qr.code_uuid}</code>
            </td>
            <td>
                <span className={`badge ${qr.is_car ? 'badge-warning' : 'badge-warning'}`}>
                    {qr.is_car ? 'Car' : 'Bike'}
                </span>
            </td>
            <td>
                <span className={`badge ${qr.is_active ? 'badge-success' : 'badge-error'}`}>
                    {qr.is_active ? 'Active' : 'Inactive'}
                </span>
            </td>
            <td>
                {qr.linking ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '600' }}>
                            {[qr.linking.first_name, qr.linking.last_name].filter(Boolean).join(' ') || qr.linking.vehicle_number || 'Linked'}
                        </span>
                        {qr.linking.mobile && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                {qr.linking.mobile}
                            </span>
                        )}
                    </div>
                ) : (
                    <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        In Inventory
                    </span>
                )}
            </td>
            <td>{qr.linking?.vehicle_number || '-'}</td>
            <td>{formatDate(qr.created_at)}</td>
            <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {qr.last_printed_at ? formatDateTime(qr.last_printed_at) : '—'}
            </td>
        </tr>
    ))

    QRTableRow.displayName = 'QRTableRow'

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        )
    }

    if (!isTagFranchise()) {
        return (
            <div className="tag-franchise-landing">
                <div className="landing-hero">
                    <h1>Become a Tag Franchise</h1>
                    <p>Start your business by distributing NAH44 safety tags in your area. Earn by providing safety solutions to vehicle owners.</p>
                </div>

                <div className="features-grid">
                    <div className="feature-card">
                        <div className="feature-icon">📦</div>
                        <h3>Bulk Inventory</h3>
                        <p>Get tags in bulk at franchise rates and manage your own distribution network.</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">💰</div>
                        <h3>Revenue Stream</h3>
                        <p>Earn through tag sales and recurring subscription services provided to customers.</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">🛠️</div>
                        <h3>Management Tools</h3>
                        <p>Access our advanced dashboard to track, assign, and manage tags in your territory.</p>
                    </div>
                </div>

                <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
                    <div className="pricing-card" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '1rem', padding: '2rem', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'left' }}>
                        <h3 style={{ color: '#60a5fa', marginBottom: '1.5rem', fontSize: '1.5rem' }}>🏍️ Bike Tags</h3>
                        <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <li style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.5rem' }}>
                                <span>Standard Tag (No Insurance)</span>
                                <strong style={{ color: 'white' }}>₹499</strong>
                            </li>
                            <li style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.5rem' }}>
                                <span>Tag + ₹1 Lakh Insurance</span>
                                <strong style={{ color: 'white' }}>₹599</strong>
                            </li>
                            <li style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Tag + ₹2 Lakh Insurance</span>
                                <strong style={{ color: 'white' }}>₹699</strong>
                            </li>
                        </ul>
                    </div>

                    <div className="pricing-card" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '1rem', padding: '2rem', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'left' }}>
                        <h3 style={{ color: '#34d399', marginBottom: '1.5rem', fontSize: '1.5rem' }}>🚗 Car Tags</h3>
                        <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <li style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.5rem' }}>
                                <span>Standard Tag (No Insurance)</span>
                                <strong style={{ color: 'white' }}>₹599</strong>
                            </li>
                            <li style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Tag + ₹3 Lakh Insurance</span>
                                <strong style={{ color: 'white' }}>₹799</strong>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="request-card">
                    <h2>Submit Franchise Request</h2>
                    <form onSubmit={handleRequestTags}>
                        <div className="grid grid-2" style={{ gap: '1rem', textAlign: 'left' }}>
                            <div className="form-group">
                                <label>Initial Bike Tags</label>
                                <input
                                    type="number"
                                    min="50"
                                    value={requestForm.bikeCount}
                                    onChange={e => setRequestForm({ ...requestForm, bikeCount: parseInt(e.target.value) })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Initial Car Tags</label>
                                <input
                                    type="number"
                                    min="50"
                                    value={requestForm.carCount}
                                    onChange={e => setRequestForm({ ...requestForm, carCount: parseInt(e.target.value) })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginTop: '1rem', textAlign: 'left' }}>
                            <label>Shipping Address</label>
                            <textarea
                                value={requestForm.shippingAddress}
                                onChange={e => setRequestForm({ ...requestForm, shippingAddress: e.target.value })}
                                placeholder="Where should we send your starter kit?"
                                rows="3"
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginTop: '1rem', textAlign: 'left' }}>
                            <label>Message (Optional)</label>
                            <textarea
                                value={requestForm.message}
                                onChange={e => setRequestForm({ ...requestForm, message: e.target.value })}
                                placeholder="Tell us about your experience or business plans..."
                                rows="2"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn-primary"
                            style={{ width: '100%', marginTop: '2rem', padding: '1rem' }}
                            disabled={requestSubmitting}
                        >
                            {requestSubmitting ? 'Submitting...' : 'Send Franchise Application'}
                        </button>
                    </form>
                </div>
            </div>
        )
    }

    return (
        <div className="container admin-qrcodes" style={{ paddingTop: '2rem' }}>
            <div className="admin-header qr-header-section">
                <div>
                    <h1>Franchise Tags</h1>
                    <p className="admin-subtitle">View and print QR tags available in the system</p>
                </div>
                <div className="qr-header-actions">
                    <button
                        type="button"
                        className="btn-primary btn-success"
                        onClick={handlePrint}
                    >
                        🖨️ Print tags
                        {selectedCodes.size > 0 ? ` (${selectedCodes.size} selected)` : ''}
                    </button>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="qr-filters">
                    <input
                        type="text"
                        placeholder="Search by UUID or Vehicle Number..."
                        value={searchInput}
                        onChange={handleSearchChange}
                    />
                    <select
                        value={filters.is_active}
                        onChange={(e) => handleFilterChange('is_active', e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                    <select
                        value={filters.is_car}
                        onChange={(e) => handleFilterChange('is_car', e.target.value)}
                    >
                        <option value="">All Types</option>
                        <option value="true">Car</option>
                        <option value="false">Bike</option>
                    </select>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">
                        Tags ({pagination.total})
                        {selectedCodes.size > 0 && (
                            <span className="selected-count">
                                {selectedCodes.size} selected
                            </span>
                        )}
                    </h2>
                </div>
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={selectedCodes.size === qrcodes.length && qrcodes.length > 0}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th>ID</th>
                                <th>QR Code</th>
                                <th>Code UUID</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Assigned To</th>
                                <th>Vehicle Number</th>
                                <th>Created At</th>
                                <th>Last printed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {qrcodes.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="empty-state">
                                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📱</div>
                                        <p>No tags found</p>
                                    </td>
                                </tr>
                            ) : (
                                qrcodes.map(qr => (
                                    <QRTableRow
                                        key={qr.id}
                                        qr={qr}
                                        isSelected={selectedCodes.has(qr.id)}
                                        onSelect={() => handleSelectOne(qr.id)}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="pagination-footer" style={{ 
                    marginTop: '2rem', 
                    paddingTop: '1.5rem', 
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem'
                }}>
                    <div className="pagination-info" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        Showing <strong>{((pagination.page - 1) * pagination.limit) + 1}</strong> - <strong>{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> of <strong>{pagination.total}</strong> tags
                    </div>

                    <div className="pagination-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div className="limit-selector" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Per page:</span>
                            <select 
                                value={pagination.limit}
                                onChange={(e) => {
                                    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))
                                }}
                                style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: 'white',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '0.4rem',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>

                        <div className="pagination-buttons" style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="btn-secondary"
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                                style={{ padding: '0.5rem 1rem' }}
                            >
                                Previous
                            </button>
                            <span style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '0 1rem',
                                background: 'rgba(var(--primary-rgb), 0.1)',
                                borderRadius: '0.5rem',
                                color: 'var(--primary-color)',
                                fontWeight: '600'
                            }}>
                                {pagination.page} / {pagination.pages || 1}
                            </span>
                            <button
                                className="btn-secondary"
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.pages || pagination.pages === 0}
                                style={{ padding: '0.5rem 1rem' }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Print Options Modal */}
            {isPrintModalOpen && (
                <div className="modal-overlay" onClick={() => setIsPrintModalOpen(false)}>
                    <div
                        className="modal-content print-stickers-modal"
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '520px', width: '100%' }}
                    >
                        <div className="modal-header">
                            <h2>Print stickers</h2>
                            <button type="button" className="modal-close" onClick={() => setIsPrintModalOpen(false)}>×</button>
                        </div>

                        <div className="print-stickers-modal-body">
                        <div className="form-group">
                            <label>Sticker Type</label>
                            <div className="vehicle-type-selector" style={{ marginTop: '0.5rem' }}>
                                <label className="vehicle-type-label">
                                    <input
                                        type="radio"
                                        name="printType"
                                        value="bike"
                                        checked={printOptions.type === 'bike'}
                                        onChange={() => setPrintOptions(prev => ({ ...prev, type: 'bike' }))}
                                    />
                                    <span>Bike (2.5" x 4")</span>
                                </label>
                                <label className="vehicle-type-label">
                                    <input
                                        type="radio"
                                        name="printType"
                                        value="car"
                                        checked={printOptions.type === 'car'}
                                        onChange={() => setPrintOptions(prev => ({ ...prev, type: 'car' }))}
                                    />
                                    <span>Car (2.5" x 4")</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginTop: '1.5rem' }}>
                            <label>Copies per selected tag</label>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={printOptions.copies}
                                onChange={(e) => {
                                    const n = parseInt(e.target.value, 10)
                                    const copiesVal = Number.isFinite(n) ? Math.min(50, Math.max(1, n)) : 1
                                    setPrintOptions(prev => ({ ...prev, copies: copiesVal }))
                                }}
                                className="qr-count-input"
                                style={{ width: '100%', marginTop: '0.5rem' }}
                            />
                        </div>

                        <div className="form-group" style={{ marginTop: '1.25rem' }}>
                            <label>Print by database tag ID</label>
                            <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.5rem' }}>
                                Enter <strong>From</strong> and <strong>To</strong> to load your tags in that range (no checkboxes needed). Odd/even applies to that range. Leave either blank to print only checked rows.
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem', alignItems: 'center' }}>
                                <select
                                    value={printIdFilter.parity}
                                    onChange={(e) => setPrintIdFilter((p) => ({ ...p, parity: e.target.value }))}
                                    style={{
                                        padding: '0.45rem 0.6rem',
                                        borderRadius: '0.5rem',
                                        background: 'var(--bg-input)',
                                        border: '1px solid var(--border-color)',
                                        color: 'var(--text-primary)'
                                    }}
                                >
                                    <option value="all">All IDs</option>
                                    <option value="odd">Odd IDs only</option>
                                    <option value="even">Even IDs only</option>
                                </select>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                                    From
                                    <input
                                        type="number"
                                        placeholder="Min ID"
                                        value={printIdFilter.startId}
                                        onChange={(e) => setPrintIdFilter((p) => ({ ...p, startId: e.target.value }))}
                                        style={{ width: '88px', padding: '0.35rem' }}
                                    />
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                                    To
                                    <input
                                        type="number"
                                        placeholder="Max ID"
                                        value={printIdFilter.endId}
                                        onChange={(e) => setPrintIdFilter((p) => ({ ...p, endId: e.target.value }))}
                                        style={{ width: '88px', padding: '0.35rem' }}
                                    />
                                </label>
                            </div>
                            <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.35rem', marginBottom: 0 }}>
                                {buildPrintFilterSummary(printIdFilter)}
                                {rangePreviewLoading && idRangeBounds ? ' · Loading…' : (
                                    <>
                                        {' · '}
                                        <strong>{tagsForPrintModal.length}</strong> tag(s) will print
                                        {idRangeBounds && rangePreviewTruncated ? (
                                            <span style={{ color: 'var(--error-color, #f87171)' }}>
                                                {' '}(over {rangePreviewMax} — narrow the range)
                                            </span>
                                        ) : null}
                                    </>
                                )}
                            </p>
                        </div>

                        <div style={{ marginTop: '1rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem', display: 'block' }}>
                                {idRangeBounds
                                    ? `Tags in this print job (${tagsForPrintModal.length} from your inventory range)`
                                    : `Tags in this print job (${tagsForPrintModal.length} of ${selectedCodes.size} checked)`}
                            </label>
                            <div
                                style={{
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '0.5rem',
                                    padding: '0.35rem 0.5rem',
                                    background: 'var(--bg-input)',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {rangePreviewLoading && idRangeBounds ? (
                                    <div style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>Loading tags…</div>
                                ) : printModalListPreview.length === 0 ? (
                                    <div style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>
                                        {idRangeBounds ? 'No tags in this range.' : 'Select tags in the table, or set From and To.'}
                                    </div>
                                ) : (
                                    <>
                                        {printModalListPreview.map(qr => {
                                        const vehicle = qr.linking?.vehicle_number
                                        const owner = qr.linking
                                            ? [qr.linking.first_name, qr.linking.last_name].filter(Boolean).join(' ') || qr.linking.mobile
                                            : qr.owner_business_name || qr.owner_name
                                        return (
                                            <div
                                                key={qr.id}
                                                style={{
                                                    padding: '0.4rem 0.25rem',
                                                    borderBottom: '1px solid var(--border-color)',
                                                    lineHeight: 1.35
                                                }}
                                            >
                                                <span style={{ fontWeight: 700 }}>#{qr.id}</span>
                                                {' · '}
                                                <code className="code-uuid" style={{ fontSize: '0.75rem' }}>{qr.code_uuid}</code>
                                                {vehicle && (
                                                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                                                        Vehicle: {vehicle}
                                                    </span>
                                                )}
                                                {owner && (
                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', display: 'block' }}>
                                                        {owner}
                                                    </span>
                                                )}
                                                <span style={{ color: 'var(--primary-color)', fontSize: '0.75rem' }}>
                                                    × {printOptions.copies} sticker{printOptions.copies !== 1 ? 's' : ''} · ref TAG · {tagPrintRef(qr.code_uuid)}
                                                </span>
                                            </div>
                                        )
                                    })}
                                        {printModalListOmit > 0 && (
                                            <div style={{ padding: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                … and {printModalListOmit} more (all included when you print)
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
                                Total stickers: {tagsForPrintModal.length * printOptions.copies} · Sheet: 13×19 in · Summary in print tab (not on paper). Last printed updates after print.
                            </p>
                        </div>
                        </div>

                        <div className="print-stickers-modal-actions">
                            <button type="button" className="btn-secondary" onClick={() => setIsPrintModalOpen(false)} style={{ flex: 1 }}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={executePrint}
                                disabled={
                                    rangePreviewLoading ||
                                    rangePreviewTruncated ||
                                    (idRangeBounds ? rangePreviewTags.length === 0 : tagsMatchingPrintFilters.length === 0)
                                }
                                style={{ flex: 1 }}
                            >
                                Print Now
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tag Request Modal */}
            {isRequestModalOpen && (
                <div className="modal-overlay" onClick={() => setIsRequestModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2>Request New Tags</h2>
                            <button className="modal-close" onClick={() => setIsRequestModalOpen(false)}>×</button>
                        </div>

                        <form onSubmit={handleRequestTags}>
                            <div className="grid grid-2" style={{ gap: '1rem' }}>
                                <div className="form-group">
                                    <label>Bike Tags Quantity</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={requestForm.bikeCount}
                                        onChange={e => setRequestForm({ ...requestForm, bikeCount: parseInt(e.target.value) })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Car Tags Quantity</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={requestForm.carCount}
                                        onChange={e => setRequestForm({ ...requestForm, carCount: parseInt(e.target.value) })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group" style={{ marginTop: '1rem' }}>
                                <label>Shipping Address (Details for delivery)</label>
                                <textarea
                                    value={requestForm.shippingAddress}
                                    onChange={e => setRequestForm({ ...requestForm, shippingAddress: e.target.value })}
                                    placeholder="Enter your full business address for shipping..."
                                    rows="3"
                                    required
                                />
                            </div>

                            <div className="form-group" style={{ marginTop: '1rem' }}>
                                <label>Additional Message (Optional)</label>
                                <textarea
                                    value={requestForm.message}
                                    onChange={e => setRequestForm({ ...requestForm, message: e.target.value })}
                                    placeholder="Any specific instructions..."
                                    rows="2"
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setIsRequestModalOpen(false)}
                                    style={{ flex: 1 }}
                                    disabled={requestSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    style={{ flex: 1 }}
                                    disabled={requestSubmitting}
                                >
                                    {requestSubmitting ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default FranchiseTags
